/**
 * @name Dialog
 * @description Creates a dialog overlay from a trigger element
 */

class Dialog {
  static instances = {};

  /**
   * @param {HTMLElement} triggerElement - The element that triggers the overlay
   */
  constructor(triggerElement) {
    this.triggerElement = triggerElement;
    this.overlayConfig = JSON.parse(triggerElement.getAttribute('data-js-show-as-dialog'));
    this.overlaySourceId = this.overlayConfig.source;
    this.hideCloseButton = this.overlayConfig.hideCloseButton || false;
    this.variant = this.overlayConfig.variant || null;
    this.overlayElements = document.querySelectorAll(`[data-js-show-as-dialog-element="${this.overlaySourceId}"]`);
    this.overlayContainer = null;
    this.boundHandleBodyClick = this.handleBodyClick.bind(this);
    this.boundHandleCancel = this.handleCancel.bind(this);
    Dialog.instances[this.overlaySourceId] = this;
    this.init();
  }

  init() {
    this.initOverlay();

    this.triggerElement.addEventListener('click', (event) => {
      event.preventDefault();
      this.openOverlay();
    });
  }

  initOverlay() {
    // Check if the overlay already exists
    const existingOverlay = document.querySelector(`dialog[data-dialog-id="${this.overlaySourceId}"]`);
    if (existingOverlay) {
      this.overlayBase = existingOverlay;
      this.overlayContainer = this.overlayBase.querySelector('.dialog__inner');
      return;
    }

    // Create the overlay
    this.overlayBase = document.createElement('dialog');
    this.overlayBase.classList.add('dialog');
    if (this.variant) {
      this.overlayBase.classList.add(`dialog--${this.variant}`);
    }
    this.overlayBase.dataset.dialogId = this.overlaySourceId;
    this.overlayContainer = document.createElement('div');
    this.overlayContainer.classList.add('dialog__inner', 'theme-light', 'theme-light--10');
    this.overlayBase.appendChild(this.overlayContainer);

    if (!this.hideCloseButton) {
      const closeButton = document.createElement('button');
      closeButton.setAttribute('data-close-overlay', '');
      closeButton.setAttribute('aria-label', 'close');
      closeButton.classList.add('button', 'button--unstyled', 'dialog__close-button');
      closeButton.innerHTML = '<span class="icon icon--close"></span>';
      closeButton.addEventListener('click', this.closeOverlay.bind(this));
      this.overlayContainer.appendChild(closeButton);
    }

    this.overlayElements.forEach((element) => {
      this.overlayContainer.appendChild(element);
    });
    document.body.appendChild(this.overlayBase);
  }

  openOverlay() {
    this.overlayBase.classList.add('dialog--open');
    document.body.classList.add('er-scroll--lock');
    document.body.addEventListener('click', this.boundHandleBodyClick);
    this.overlayBase.addEventListener('cancel', this.boundHandleCancel);
    this.overlayBase.showModal();
  }
  
  closeOverlay() {
    const closeOverlayAfterTransitioned = () => {
      this.overlayBase.close();

      if (document.querySelectorAll('dialog[open]').length === 0) {
        document.body.classList.remove('er-scroll--lock');
      }
      this.overlayBase.removeEventListener('transitionend', closeOverlayAfterTransitioned);
      document.body.removeEventListener('click', this.boundHandleBodyClick);
      this.overlayBase.removeEventListener('cancel', this.boundHandleCancel);
    };

    this.overlayBase.addEventListener('transitionend', closeOverlayAfterTransitioned);
    this.overlayBase.classList.remove('dialog--open');
  }

  handleCancel(event) {
    event.preventDefault();
    this.closeOverlay();
  }

  handleBodyClick(event) {
    if (event.target === this.overlayBase) {
      this.closeOverlay();
    }
  }

  /**
   * @description Closes the dialog with the given source ID.
   * @param {string} id - The overlay source ID
   */
  static close(id) {
    Dialog.instances[id]?.closeOverlay();
  }

  /**
   * @description Initializes all elements with the data-js-show-as-dialog attribute.
   * Safe to call multiple times — already-initialized elements are skipped.
   */
  static initOverlays() {
    const triggerOverlayElements = document.querySelectorAll('[data-js-show-as-dialog]:not([data-dialog-initialized])');

    triggerOverlayElements.forEach((element) => {
      element.setAttribute('data-dialog-initialized', '');
      new Dialog(element);
    });
  }
}

document.addEventListener('DOMContentLoaded', Dialog.initOverlays);


